/**
 * TitleRow
 *
 * Editable title row for automation cards.
 * Desktop: ✏️ Name [ActionType badge] ... [Clone] [Delete]
 * Mobile: Stacks vertically with action buttons on separate row
 */

import { faClone, faPencil, faTrash } from '@fortawesome/pro-regular-svg-icons';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from 'src/components/Box';
import { Chip } from 'src/components/Chip';
import { Icon } from 'src/components/Icon';
import { IconButton } from 'src/components/IconButton';
import { TextField } from 'src/components/TextField';
import { Tooltip } from 'src/components/Tooltip';
import { Typography } from 'src/components/Typography';
import { Identifier } from 'src/types/utilityTypes';
import { formatFullDateAndTime } from 'src/utils/dateFormatter';

export interface TitleRowProps {
  name: string | null;
  actionLabel: string;
  automationId?: Identifier;
  createdAt?: number | null;
  updatedAt?: number | null;
  editing: boolean;
  onEditStart: () => void;
  onEditEnd: () => void;
  onNameChange: (name: string) => void;
  onRemove?: () => void;
  onClone?: () => void;
}

export const TitleRow: FC<TitleRowProps> = ({
  name,
  actionLabel,
  automationId,
  createdAt,
  updatedAt,
  editing,
  onEditStart,
  onEditEnd,
  onNameChange,
  onRemove,
  onClone,
}) => {
  const { t } = useTranslation();

  if (editing) {
    return (
      <Box display="flex" alignItems="center" gap={1}>
        <TextField
          value={name || ''}
          onChange={(e) => onNameChange(e.target.value)}
          placeholder={t('automations.common.enterName')}
          size="small"
          autoFocus
          onKeyDown={(e) => {
            if (e.key === 'Enter') onEditEnd();
          }}
        />
        <Typography
          variant="body2"
          color="primary"
          sx={{ cursor: 'pointer' }}
          onClick={onEditEnd}
        >
          {t('automations.common.done')}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      display="flex"
      flexDirection={{ xs: 'column', sm: 'row' }}
      alignItems={{ xs: 'flex-start', sm: 'center' }}
      gap={1}
    >
      {/* Name and Action Label */}
      <Box display="flex" alignItems="center" gap={1} flexWrap="wrap" flex={1}>
        <Box display="flex" alignItems="center">
          <Typography variant="h6">{name || t('automations.common.automation')}</Typography>
          <IconButton size="small" onClick={onEditStart}>
            <Icon icon={faPencil} size="small" />
          </IconButton>
        </Box>
        <Box display="flex" alignItems="center" gap={0.5}>
          <Typography variant="caption" color="text.secondary">
            {t('automations.common.actionLabel')}:
          </Typography>
          <Tooltip
            title={
              createdAt && !!automationId ? (
                <>
                  {t('automations.common.createdOn', { date: formatFullDateAndTime(createdAt) })}
                  {updatedAt && (
                    <>
                      <br />
                      {t('automations.common.updatedOn', { date: formatFullDateAndTime(updatedAt) })}
                    </>
                  )}
                </>
              ) : (
                t('automations.common.actionTypeTooltip', { action: actionLabel })
              )
            }
          >
            <Chip label={actionLabel} size="small" variant="outlined" />
          </Tooltip>
        </Box>
        {!automationId && (
          <Typography variant="caption" color="error">
            {t('automations.common.saveToCreateAutomation')}
          </Typography>
        )}
      </Box>

      {/* Action buttons */}
      <Box display="flex" alignItems="center" gap={0.5} flexWrap="wrap" justifyContent="flex-end">
        {onClone && (
          <Tooltip title={t('automations.common.clone')}>
            <IconButton
              color="primary"
              onClick={onClone}
              size="small"
              aria-label={t('automations.common.clone')}
            >
              <Icon icon={faClone} size="small" />
            </IconButton>
          </Tooltip>
        )}
        {onRemove && (
          <Tooltip title={t('automations.common.delete')}>
            <IconButton
              color="error"
              onClick={onRemove}
              size="small"
              aria-label={t('automations.common.delete')}
            >
              <Icon icon={faTrash} size="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    </Box>
  );
};
